import React, { useState, useEffect } from "react";
import { Box, Typography, CircularProgress, Button } from "@mui/material";
import NetworkDiagram from "./NetworkDiagram";
import { WebR } from "webr";
import { useTranslation } from "react-i18next";

import RemoveRedEyeOutlinedIcon from "@mui/icons-material/RemoveRedEyeOutlined";
import { logging } from "../../tools";
import { getLabelId } from "../moodboardModel";

const webR = new WebR({
  serviceWorkerUrl: "/",
  createLazyFilesystem: false,
});

function convertToDataframesArray(obj) {
  if (!obj || obj.type !== "list" || !Array.isArray(obj.values)) {
    return null;
  }

  return obj.values.map((dataframe) => {
    if (dataframe.type !== "list" || !Array.isArray(dataframe.values)) {
      return null;
    }

    const result = {};
    for (let i = 0; i < dataframe.names.length; i++) {
      const name = dataframe.names[i];
      const values = dataframe.values[i].values;
      result[name] = values;
    }

    return result;
  });
}

async function evalR(input) {
  await webR.init();
  logging("R input");
  logging(input);
  const result = await webR.evalR(input);
  try {
    const output = await result.toJs();
    logging("output from R");
    logging(output);
    logging("converting output");
    const convert = convertToDataframesArray(output);
    logging(convert);
    return convert; // convert to easier object
  } finally {
    logging("DESTOY WEBR");
    webR.destroy(result);
  }
}

function NetworkBoard({
  quantitativeLabelsChecked,
  corrMatrix,
  verticalTable,
}) {
  const { t } = useTranslation();
  const [input, setInput] = useState(null);
  const [result, updateResult] = useState(null); // results from webR
  const [loading, setLoading] = useState(false);

  // constructor
  useEffect(() => {
    updateResult(null);
    // create the dataset
    const getData = () => {
      let data = {};
      quantitativeLabelsChecked.forEach((selectedLabel) => {
        Object.keys(corrMatrix).forEach((labelKey) => {
          if (selectedLabel.id === labelKey)
            data[labelKey] = corrMatrix[labelKey];
        });
      });
      return data;
    };
    const getLabels = () =>
      quantitativeLabelsChecked
        .filter((selectedLabel) =>
          Object.keys(getData()).includes(selectedLabel.id)
        )
        .map((selectedLabel) => selectedLabel.label);

    const data = getData();
    const labels = JSON.stringify(getLabels());

    // create the r script
    const input = `
    # install and load the libraries
    webr::install("StrippedGraphicalVAR", repo="/repo/")
    webr::install("jsonlite")  
    library("jsonlite")
    library("StrippedGraphicalVAR") 

    ### Load the input ###
    # Define the JavaScript object as a JSON string
    json_string <- '${JSON.stringify(data)}'
    # Parse the JSON string into an R list
    data_list <- fromJSON(json_string)
    df <- data.frame(data_list)
    node_labels <- fromJSON('${labels}')

    # sort the labels
    specified_order <- c("VermoeidheidGem","Vermoeidheid","Schuld","Pieker","PosAffGemiddeld","Rust","Acceptatie","Invloed","Lichamelijke_Activiteit","Hopeloos")

    labels0 <- specified_order[specified_order %in% node_labels]
    labels1 <- node_labels[!(node_labels %in% labels0)]
    sorted_node_labels <- c(labels0, labels1)

    # sort the df
    move_cols_by_indices <- function(dataframe, moved_indices) {
      # Ensure the moved_indices are valid
      if (!all(moved_indices %in% seq_along(dataframe))) {
        stop("Invalid column indices in moved_indices")
      }
    
      # Reorder columns based on moved_indices
      dataframe <- dataframe[, moved_indices, drop = FALSE]
    
      return(dataframe)
    }
    # Find column indices based on node_labels
    original_indices <- match(node_labels, names(df))
    # Find column indices based on sorted_node_labels
    moved_indices <- match(sorted_node_labels, node_labels)
    input_df <- move_cols_by_indices(df, moved_indices)

    centerX <- 0
    centerY <- -15
    create_circle <- function(radius, num_points) {
      theta <- seq(0, 2* pi - 2 * pi / num_points, length.out = num_points)
      x <- radius * cos(theta) + centerX
      y <- radius * sin(theta) + centerY
      circle <- data.frame(x = x, y = y)
      return(circle)
    }
    create_diamond <- function(max_radius_x, max_radius_y, num_points, centerX = 0, centerY = 0) {
      theta <- seq(0, 2 * pi - 2 * pi / num_points, length.out = num_points)
    
      x <- max_radius_x * cos(theta) + centerX
      y <- max_radius_y * sin(theta) + centerY
    
      diamond <- data.frame(x = x, y = y)
      return(diamond)
    }
    
    
    radius <- 100
  
    num_points <- length(sorted_node_labels) - 1
    #coordinates <- create_circle(radius, num_points)
    coordinates <- create_diamond(max_radius_x = 350, max_radius_y = 200, num_points = num_points )  
    node_x_pos <- c(c(centerX), coordinates$x)
    node_y_pos <- c(c(centerY), coordinates$y)

    # Create settings data frame
    settings_df <- data.frame(labels= sorted_node_labels, x = node_x_pos, y=node_y_pos)
    
    # helper function to compute the partial correlation from precision matrix
    partial_correlation <- function(precision_matrix) {
      # Calculate the diagonal elements of the precision matrix
      diag_elements <- diag(precision_matrix)
      
      # Calculate the partial correlation matrix
      partial_cor_matrix <- matrix(0, nrow = ncol(precision_matrix), ncol = ncol(precision_matrix))
      
      for (i in 1:ncol(precision_matrix)) {
        for (j in 1:ncol(precision_matrix)) {
          if (i == j) {
            partial_cor_matrix[i, j] <- 1
          } else {
            partial_cor_matrix[i, j] <- -precision_matrix[i, j] / sqrt(diag_elements[i] * diag_elements[j])
          }
        }
      }
      
      return(partial_cor_matrix)
    }
    
    doEBICglasso <-function(S,nobs,penalize.diagonal=TRUE){
      S=cov2cor(S)
      nlam=100
      lambdas=calcPath(S,nlam=nlam)
      
      glas_path <- list(
        w = array(0, c(ncol(S), ncol(S), length(lambdas))),
        wi = array(0, c(ncol(S), ncol(S), length(lambdas))),
        rho = lambdas,
        EBIC = Inf*lambdas
      )
      
      for (i in 1:nlam){
        rhoMat <- matrix(lambdas[i], ncol(S), ncol(S))
        
        #penalize diagonal
        if (penalize.diagonal==FALSE){
          diag(rhoMat) <- 0
        }
        
        res <- StrippedGraphicalVAR:::glasso(S, rhoMat)
        
        w=res$w
        wi=res$wi
        
        glas_path$w[,,i] <- w
        glas_path$wi[,,i] <- wi
        glas_path$EBIC[i] <- EBIC(S,wi,nobs)
      }
      idx=which.min(glas_path$EBIC)
      
      return(glas_path$wi[,,idx])
      #return(glas_path)
    }
    
    #' @noRd
    # Extended Bayesian Information Criterion ----
    # Here for legacy (vectorization applied in function)
    # Updated 18.06.2023
    EBIC <- function(S, K, n, p, gamma = 0.5, E, countDiagonal = FALSE)
    {
      
      # Obtain likelihood
      L <- logGaus(S, K, n)
      
      # Determine if number of edges is missing
      ## Computes edges and avoids check 
      E <- sum(K[lower.tri(K, diag = countDiagonal)] != 0)
      
      # Number of nodes
      p <- ncol(K)
      
      # Return EBIC
      return(
        -2 * L + E * log(n) + 4 * E * gamma * log(p)
      )
      
    }
    
    #' @noRd
    # Log-likelihood ----
    # According to huge??? : source comment
    # Updated 07.08.2023
    logGaus <- function(S, K, n)
    {
      tr = function(A) sum(diag(A))  
      return(0.5 * n * (log(det(K)) - tr(S %*% K)))
      
    }
    
    calcPath<-function(S,lam=NULL,lam.min.ratio=0.01,nlam=10){
      Sminus = S
      diag(Sminus) = 0
      
      # compute grid of lam values, if necessary
      if (is.null(lam)) {
        if (!((lam.min.ratio <= 1) && (lam.min.ratio > 0))) {
          cat("lam.min.ratio must be in (0, 1]... setting to 1e-2!\n\n")
          lam.min.ratio = 0.01
        }
        if (!((nlam > 0) && (nlam%%1 == 0))) {
          cat("nlam must be a positive integer... setting to 10!\n\n")
          nlam = 10
        }
        
        # calculate lam.max and lam.min
        lam.max = max(abs(Sminus))
        lam.min = lam.min.ratio * lam.max
        
        # calculate grid of lambda values
        lam = 10^seq(log10(lam.min), log10(lam.max), length = nlam)
        
      }
    }
    
    ### Do calculations here ###
    df_filtered <- cov(input_df, use="pairwise.complete.obs")
    # Remove rows and columns with NaN values
    df_filtered <- df_filtered[complete.cases(df_filtered), colSums(is.na(df_filtered)) == 0]
    #result <- covglasso(S=df_filtered, L=1000, crit="ebic", n=nrow(input_df))
    precision_matrix <- doEBICglasso(df_filtered, nrow(input_df),penalize.diagonal=TRUE)
    PartialCorMatrix <- partial_correlation(precision_matrix)
    output_df <- as.data.frame(PartialCorMatrix)
    
    for (i in 1:ncol(input_df)) {
      temp=na.omit(input_df[,i])
      temp=cor.test(temp[1:(length(temp)-1)],temp[2:(length(temp))])
      if (temp$p.value<0.1){
        output_df[i,i]=temp$estimate
      }else{
        output_df[i,i]=0
      }
    }
    
    ### Create output ###
    # Create a list to store the data frames
    list_of_dataframes <- list(settings = settings_df, input = input_df, output = output_df)                    
    # Print all dataframes in one statement (do not change the end of script)
    lapply(list_of_dataframes, print)
`;
    setInput(input);
  }, [quantitativeLabelsChecked, corrMatrix, verticalTable]);

  async function press() {
    setLoading(true);
    let values = null;
    try {
      values = await evalR(input);
    } catch {}
    updateResult(values);
    setLoading(false);
  }

  /*   useEffect(() => {
    if (!input) return;
    (async () => {
      const values = await evalR(input);
      updateResult(values);
    })();
  }, [input]); */

  if (!result)
    return (
      <Box
        display={"flex"}
        alignItems={"center"}
        justifyContent={"center"}
        height={500}
      >
        {loading ? (
          <Box textAlign={"center"}>
            <CircularProgress />
            <Typography variant="body2">
              {t("common.notifications.loading")}
            </Typography>
          </Box>
        ) : (
          <Box textAlign={"center"}>
            <Button
              startIcon={<RemoveRedEyeOutlinedIcon />}
              variant="outlined"
              onClick={press}
            >
              {t("moodboards.buttons.view")}
            </Button>
          </Box>
        )}
      </Box>
    );
  return (
    <Box>
      <NetworkDiagram settings={result[0]} result={result[2]} />
    </Box>
  );
}

export default NetworkBoard;
